import { FCAdditionalProps } from "@datassential/platform-ui-lib";
import ContentWidget from "@components/ContentWidget/ContentWidget";
import Title from "@components/Title";
import cn from "classnames";
import './Experience.scss';
import Metric from "@pages/Profile/components/Experience/Metric";
import { useCategory, useCommonDataContext } from "@core/CommonDataContext";
import { FC, useMemo } from "react";
import {
    compareMetrics,
    getMetricColor,
    PROFILE_EXPERIENCE_METRICS_ORDER
} from "@models/Metric";
import { useProfileContext } from "@pages/Profile/ProfileContext";
import { ProfileExperienceRequest, useProfileExperience } from "@core/api/useProfileExperience";
import { ALL_FOOD_BEVERAGE_CATEGORY } from "@core/constants";
import { ProfileDataResponseItem } from "@core/api/useProfileHeader";
import { useSelectAmongContext } from "@vms/SelectAmong/SelectAmongContext";

type ExperienceProps = Omit<FCAdditionalProps, 'id'> & {
    id: ProfileDataResponseItem["id"];
}

const Experience: FC<ExperienceProps> = ({
    id,
    className,
    testId = "profileExperience",
    ...props
}) => {
    const { metrics: metricsConfig } = useCommonDataContext();
    const {
        categoryId,
    } = useProfileContext();

    const category = useCategory(categoryId);
    const selectedCategoryTitle = useMemo(() => (
        (category ? category?.name : ALL_FOOD_BEVERAGE_CATEGORY)?.toUpperCase()
    ), [category]);

    const { amongPayload } = useSelectAmongContext();
    const payload = useMemo(
        (): ProfileExperienceRequest => ({
            ...amongPayload,
            id,
            categoryId,
        }),
        [amongPayload, id, categoryId]
    );

    const {
        data: rawMetrics,
        isLoading
    } = useProfileExperience(payload);

    const metrics = useMemo(
        () => {
            return (rawMetrics ?? [])
                .map((i) => {
                    const o = (metricsConfig ?? []).find(o => o.id === i.metric);
                    return {
                        id: o?.id,
                        color: getMetricColor(o?.id),
                        value: i.raw ?? 0,
                        title: o?.name,
                        percentileValue: i.norm ?? 0,
                        rank: i.rank ?? 0,
                        rankTotal: i.rankTotal ?? 0,
                        tooltipTitle: i.tooltip?.title ?? '',
                        tooltipDescription: i.tooltip?.description ?? '',
                    };
                })
                .sort((a, b) => {
                    return compareMetrics(PROFILE_EXPERIENCE_METRICS_ORDER, a.id, b.id);
                });
        },
        [rawMetrics, metricsConfig]
    );

    return (
        <ContentWidget
            className={cn(
                "ProfileExperience",
                "appears-right", "del-2",
                className,
            )}
            testId={testId}
            isLoading={isLoading}
            {...props}
        >
            <div className="ContentWidget__header">
                <Title testId="textProfileExpTitle">
                    EXPERIENCE
                </Title>
            </div>
            <div className="ProfileExperience__metrics">
                {metrics.map((metric, idx) => (
                    <div key={metric.id}>
                        <Metric
                            color={metric.color}
                            metricId={metric.id}
                            value={metric.value}
                            title={metric.title}
                            versus={selectedCategoryTitle}
                            tooltipTitle={metric.tooltipTitle}
                            tooltipDescription={metric.tooltipDescription}
                            percentileValue={metric.percentileValue}
                            rank={metric.rank}
                            rankTotal={metric.rankTotal}
                            testId={`blockProfileExpMetric${idx}`}
                        />
                    </div>
                ))}
            </div>
        </ContentWidget>
    );
};

export default Experience;